import { MouseDragStatus } from "@/lib/structure-panel-drag/util";
import { findNode, getNodeIndex, getNodeParent } from "@/lib/tree/tree";
import { Tree } from "@/types/tree";
import { isThisNodeAllowedInsideThatNode } from "../tree/nesting";
import { structurePanelNodeIsCollapsed } from "@/lib/structure-panel/expand-collapse";

type NodePosition = {
  parentId: number;
  index: number;
};

export function getNewNodePosition(
  mouseDragStatus: MouseDragStatus,
  tree: Tree,
  draggedNodeId: number
): NodePosition | false {
  let maybeNodePosition: false | NodePosition = false;
  const draggedNode = findNode(tree, draggedNodeId);
  if (!draggedNode) return false;

  /*
    
    keep this comment in sync with the drag logic or ur a huge asshole
  
    if mouse is over another node
      if over the top half of a node
        position it above that node (as the preceding sibling) - if it's allowed
         - the logic to do that is if it is already positioned above the node we are over, dont do shit
         - and otherwise, move it to the position of the node we are over (occupying its index will push it down one)
  
        if it can not be positioned above that node as the preceding sibling,
          does that node have a preceding sibling? can we position it as the last child of that node's preceding sibling?
  
  
      if over the bottom half of a node and the node has no children showing (i.e. the node has no children, or the node is collapsed)
        position it below that node (as the following sibling) - if it's allowed.
        (A) the logic for this is as follows:
          ... put it at the position returned by getPositionAfterNode, but we have to subtract one from the index returned...
          ... by getPositionAfterNode if the ghost node is already inside the parent of the node we are dragging over...
          ... and its index is lower that the index of the node we are dragging over ...
          ... because the position returned by getPositionAfterNode isn't taking into account ...
          ... that the ghost node is't then gonna be removed from above it ...
  
        if it can not be positioned below that node
          position it inside that node as the first child
  
        if it can't be positioned inside that node as the first child:
          if the node is the last child of its parent (B)
            try to position it after the node's parent
              if it's not allowed, recurse to B, with the node parent's parent
              note: 
              ... just like (A) above, we have to subtract one from the node index returned by
              ... getPositionAfterNode if the ghost node is in the same parent that the node is being dragged to
              ... at a lower position than the node's new index, because getPositionAfterNode doesn't take 
              ... into account that the ghost node is going to be removed from above it

      if over the bottom half of a node, and the node has children
        position it inside that node, as the first child
  
    if mouse is over the dragged node
      if it is the last child, and mouse is trying to pull it shallower
        position it below its parent (as the following sibling) - recursively.
  
      if it has a preceding sibling (meaning it isnt the first child), and mouse is trying to push it deeper
        position it inside its preceding sibling, as the last child - (TODO: right now this isn't recursive, but it should be)
  
    finally, check if where it has been positioned is allowed, and if so, return that position
    */

  if (!mouseDragStatus.overSelf) {
    if (mouseDragStatus.overTopHalf) {
      maybeNodePosition = getPositionOfNode(mouseDragStatus.overTopHalf, tree);

      if (
        draggedNodeId ===
        getIdOfPrecedingSibling(mouseDragStatus.overTopHalf, tree)
      ) {
        maybeNodePosition = false;
      }

      const precedingSiblingId = getIdOfPrecedingSibling(
        mouseDragStatus.overTopHalf,
        tree
      );

      if (
        maybeNodePosition &&
        precedingSiblingId &&
        isThisNodeAllowedInsideThatNode(tree, draggedNode, precedingSiblingId)
      ) {
        maybeNodePosition = getPositionAtEndInside(precedingSiblingId, tree);
      }
    } else if (
      mouseDragStatus.overBottomHalf &&
      (nodeChildrenCount(mouseDragStatus.overBottomHalf, tree) === 0 ||
        structurePanelNodeIsCollapsed(mouseDragStatus.overBottomHalf))
    ) {
      maybeNodePosition = getPositionAfterNode(
        mouseDragStatus.overBottomHalf,
        tree
      );

      if (
        maybeNodePosition &&
        isThisNodeAllowedInsideThatNode(
          tree,
          draggedNode,
          maybeNodePosition.parentId
        )
      ) {
        const index = getNodeIndex(tree, draggedNode.id);
        const parent = getNodeParent(tree, draggedNode.id);

        if (
          index !== false &&
          parent !== false &&
          maybeNodePosition &&
          mouseDragStatus.overIndex
        ) {
          if (maybeNodePosition.parentId === parent.id) {
            if (index < mouseDragStatus.overIndex) {
              maybeNodePosition.index = maybeNodePosition.index - 1;
            }
          }
        }
      } else if (
        maybeNodePosition &&
        !isThisNodeAllowedInsideThatNode(
          tree,
          draggedNode,
          maybeNodePosition.parentId
        )
      ) {
        maybeNodePosition = getPositionAtStartInside(
          mouseDragStatus.overBottomHalf
        );
      }

      if (
        maybeNodePosition &&
        !isThisNodeAllowedInsideThatNode(
          tree,
          draggedNode,
          maybeNodePosition.parentId
        ) &&
        isLastChild(mouseDragStatus.overBottomHalf, tree)
      ) {
        let parentToPositionAfter: number | false = false;

        if (mouseDragStatus.overBottomHalf) {
          const parentToPositionAfter_ = getNodeParent(
            tree,
            mouseDragStatus.overBottomHalf
          );
          parentToPositionAfter = parentToPositionAfter_
            ? parentToPositionAfter_.id
            : false;
        }

        while (parentToPositionAfter) {
          maybeNodePosition = getPositionAfterNode(parentToPositionAfter, tree);

          // subtract one from index logic
          const parent = getNodeParent(tree, draggedNode.id);

          if (
            maybeNodePosition &&
            parent &&
            maybeNodePosition.parentId === parent.id
          ) {
            const draggedNodeIndex = getNodeIndex(tree, draggedNode.id);
            const lastRecursedItemIndex = getNodeIndex(
              tree,
              parentToPositionAfter
            );

            if (draggedNodeIndex < lastRecursedItemIndex) {
              maybeNodePosition.index = maybeNodePosition.index - 1;
            }
          }
          // ------

          if (!maybeNodePosition) {
            parentToPositionAfter = false;
          } else if (
            !isThisNodeAllowedInsideThatNode(
              tree,
              draggedNode,
              maybeNodePosition.parentId
            ) &&
            isLastChild(parentToPositionAfter, tree)
          ) {
            const parent = getNodeParent(tree, parentToPositionAfter);

            parentToPositionAfter = parent ? parent.id : false;
          } else {
            parentToPositionAfter = false;
          }
        }
      }
    } else if (
      mouseDragStatus.overBottomHalf &&
      nodeChildrenCount(mouseDragStatus.overBottomHalf, tree) > 0
    ) {
      maybeNodePosition = getPositionAtStartInside(
        mouseDragStatus.overBottomHalf
      );
    }
  } else if (mouseDragStatus.overSelf) {
    if (
      isLastChild(draggedNodeId, tree) &&
      mouseDragStatus.overNestShallower === draggedNodeId
    ) {
      let positionInsideParentNodeId: number | false = false;

      maybeNodePosition = getPositionBelowParent(draggedNodeId, tree);

      if (maybeNodePosition) {
        positionInsideParentNodeId = maybeNodePosition.parentId;
      }

      while (positionInsideParentNodeId) {
        if (
          isThisNodeAllowedInsideThatNode(
            tree,
            draggedNode,
            positionInsideParentNodeId
          )
        ) {
          break;
        } else if (
          maybeNodePosition &&
          !isThisNodeAllowedInsideThatNode(
            tree,
            draggedNode,
            maybeNodePosition.parentId
          ) &&
          isLastChild(positionInsideParentNodeId, tree)
        ) {
          const parent = getNodeParent(tree, positionInsideParentNodeId);
          positionInsideParentNodeId = parent ? parent.id : false;

          if (positionInsideParentNodeId) {
            maybeNodePosition = getPositionAtEndInside(
              positionInsideParentNodeId,
              tree
            );
          }
        } else {
          break;
        }
      }
    }

    if (
      hasPrecedingSibling(draggedNodeId, tree) &&
      mouseDragStatus.overNestDeeper === draggedNodeId
    ) {
      maybeNodePosition = getPositionAtEndInsidePrecedingSibling(
        draggedNodeId,
        tree
      );
    }
  }

  if (!maybeNodePosition) return false;

  const dragAllowed = isThisNodeAllowedInsideThatNode(
    tree,
    draggedNode,
    maybeNodePosition.parentId
  );

  if (dragAllowed) {
    return maybeNodePosition;
  }
  return false;
}

/* helper functions */

function getPositionOfNode(nodeId: number, tree: Tree): NodePosition | false {
  const index = getNodeIndex(tree, nodeId);
  const parent = getNodeParent(tree, nodeId);

  if (index === false || parent === false) return false;

  return {
    parentId: parent.id,
    index: index
  };
}

function getPositionAfterNode(
  nodeId: number,
  tree: Tree
): NodePosition | false {
  const index = getNodeIndex(tree, nodeId);
  const parent = getNodeParent(tree, nodeId);

  if (index === false || parent === false) return false;

  return {
    parentId: parent.id,
    index: index + 1
  };
}

function getPositionAtStartInside(nodeId: number): NodePosition {
  return {
    parentId: nodeId,
    index: 0
  };
}

function getPositionAtEndInside(
  nodeId: number,
  tree: Tree
): NodePosition | false {
  const childCount = nodeChildrenCount(nodeId, tree);
  if (childCount === false) return false;

  return {
    parentId: nodeId,
    index: childCount
  };
}

function getPositionBelowParent(
  nodeId: number,
  tree: Tree
): NodePosition | false {
  const parent = getNodeParent(tree, nodeId);
  if (!parent) return false;

  return getPositionAfterNode(parent.id, tree);
}

function getPositionAtEndInsidePrecedingSibling(
  nodeId: number,
  tree: Tree
): NodePosition | false {
  const precedingSiblingId = getIdOfPrecedingSibling(nodeId, tree);
  if (!precedingSiblingId) return false;

  return getPositionAtEndInside(precedingSiblingId, tree);
}

function getIdOfPrecedingSibling(nodeId: number, tree: Tree) {
  const index = getNodeIndex(tree, nodeId);
  const parent = getNodeParent(tree, nodeId);

  if (index === false || parent === false) return false;

  if (index < 1) return false;

  return parent.children[index - 1].id;
}

function nodeChildrenCount(nodeId: number, tree: Tree) {
  const node = findNode(tree, nodeId);
  if (!node) return false;
  return node.children.length;
}

function isLastChild(nodeId: number, tree: Tree) {
  const index = getNodeIndex(tree, nodeId);
  const parent = getNodeParent(tree, nodeId);

  if (index === false || parent === false) return;

  const childCount = nodeChildrenCount(parent.id, tree);
  if (!childCount) return;

  return childCount === 1 || index === childCount - 1;
}

function hasPrecedingSibling(nodeId: number, tree: Tree) {
  const index = getNodeIndex(tree, nodeId);

  if (index === false) return;

  return index > 0;
}
